import {
  AccountingAgendaSlotDoctolibInvalidErrorData,
  BUSINESS_TIMEZONE,
} from '@omecare/utils';
import { intlFormat } from 'date-fns/intlFormat';
import { EyeIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { pushSheet } from '@/components/sheets';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getOfficeAgendaSlotById } from '@/hooks/queries/use-get-agenda-slot-by-id';

export const ImportAccountingAgendaSlotListReport = ({
  title,
  agendaSlots,
  officeId,
  context,
}: {
  title: string;
  officeId: string;
  agendaSlots: AccountingAgendaSlotDoctolibInvalidErrorData[];
  context?: {
    explanationKey: string;
    warningType: string;
  };
}) => {
  const { t } = useTranslation();

  if (agendaSlots.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <span className="text-muted-foreground text-sm">
          {t('admin.dashboard.agenda-slots-pending.list-empty')}
        </span>
      </div>
    );
  }

  const viewModel = (() => {
    return {
      openSlotModal: async (
        slot: AccountingAgendaSlotDoctolibInvalidErrorData,
      ) => {
        const slotInformation = await getOfficeAgendaSlotById(slot.id)();
        pushSheet('ViewAdminAgendaSlotSheet', {
          officeId,
          slot: slotInformation,
          eventOccurrence: {
            id: null,
            start: new Date(slotInformation.startAt),
            end: new Date(slotInformation.endAt),
            allday: slotInformation.periodOfTheDay === 'allday',
            index: 0,
          },
        });
      },
    };
  })();

  return (
    <Card
      className="md:max-h-[350px] lg:max-h-[450px]"
      x-chunk="dashboard-01-chunk-0"
    >
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <div className="flex flex-col gap-2">
          <CardTitle className="font-semibold text-md">{t(title)}</CardTitle>
          {context && (
            <p className="text-muted-foreground text-sm">
              {t(`http.errors.${context.explanationKey}`)}
            </p>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-3">
        <div className="h-40 max-h-80 overflow-y-auto">
          <div className="flex flex-col gap-6">
            {agendaSlots
              .map((item) => {
                return {
                  ...item,
                  date: intlFormat(item.startAt, {
                    timeZone: BUSINESS_TIMEZONE,
                    year: 'numeric',
                    month: 'numeric',
                    day: 'numeric',
                  }),
                  period: t(`period-of-day.${item.periodOfTheDay}`),
                };
              })
              .map((item) => {
                return (
                  <div
                    key={item.id}
                    className="flex items-center gap-6"
                  >
                    <div className="grid flex-1 gap-1">
                      <p className="font-medium text-sm leading-none">
                        {item.date} {item.period}
                      </p>
                      <p className="text-muted-foreground text-sm">
                        {item.type}
                      </p>
                    </div>

                    <div className="flex-none">
                      <Button
                        type="submit"
                        className="flex-1 gap-2"
                        onClick={() => viewModel.openSlotModal(item)}
                      >
                        <EyeIcon className="h-5 w-5" />
                        <span className="hidden xl:block">
                          {t('btn.view-details')}
                        </span>
                      </Button>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
